import { guardAdminNavigation, useAdminDraft } from './UnsavedChanges';
import './RoleManagement.css';
import AdminRolePreview from './AdminRolePreview';
import React, { useEffect, useId, useState } from 'react';
import Select from 'react-select';
const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const labels = { user: 'Cliente', admin: 'Administrador de Wisby', abogado_wisby: 'Abogado de Wisby', notario: 'Notario titular', admin_notaria: 'Administrador de notaría', abogado_notaria: 'Abogado de Notaría' };
export default function RoleManagement({ team = false, embedded = false, refreshKey = 0 }) {
  const [data, setData] = useState({ users: [], notarios: [] });
  const [invitations, setInvitations] = useState([]);
  const [form, setForm] = useState({ email: '', role: team ? 'abogado_notaria' : 'abogado_wisby', notarioId: '', additionalRoles: [] });
  const roleInputId = useId();
  const roleOptions = Object.entries(labels).filter(([key]) => key !== 'admin_notaria' && (!team || key === 'abogado_notaria')).map(([value, label]) => ({ value, label }));
  const selectedRoles = [form.role, ...form.additionalRoles].filter(Boolean);
  const markRoleSaved = useAdminDraft(form, team, Boolean(form.email));
  const [message, setMessage] = useState('');
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lookupStatus, setLookupStatus] = useState('idle');

  const request = async (path = '', options = {}) => {
    const res = await fetch(`${API}/api/roles${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': localStorage.getItem('token') || sessionStorage.getItem('token'),
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.msg || 'No se pudo completar la operación.');
    return json;
  };

  const refresh = async () => {
    const [users, pending] = await Promise.all([request(), request('/invitations')]);
    setData(users);
    setInvitations(pending.invitations || []);
  };
  useEffect(() => {
    let active = true;
    Promise.all([request(), request('/invitations')])
      .then(([users, pending]) => {
        if (active) { setData(users); setInvitations(pending.invitations || []); }
      })
      .catch(error => { if (active) { setMessage(error.message); setMessageSuccess(false); } });
    return () => { active = false; };
  }, [refreshKey]);

  useEffect(() => {
    const email = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) {
      setLookupStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setLookupStatus('loading');
    const timer = setTimeout(() => {
      request(`/lookup?email=${encodeURIComponent(email)}`)
        .then(value => { if (!cancelled) setLookupStatus(value.exists ? 'existing' : 'missing'); })
        .catch(() => { if (!cancelled) setLookupStatus('error'); });
    }, 650);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.email, refreshKey]);

  const save = async event => {
    event.preventDefault();
    if (!selectedRoles.length) { setMessage('Selecciona al menos un rol.'); setMessageSuccess(false); return; }
    if (lookupStatus !== 'existing' && lookupStatus !== 'missing') {
      setMessage('Comprueba el correo antes de continuar.'); setMessageSuccess(false); return;
    }
    setBusy(true);
    setMessage('');
    try {
      const inviting = lookupStatus === 'missing';
      const result = await request(inviting ? '/invitations' : '', {
        method: inviting ? 'POST' : 'PUT',
        body: JSON.stringify(form),
      });
      await refresh();
      markRoleSaved();
      setMessageSuccess(true);
      setMessage(result.msg || (inviting ? 'Invitación enviada correctamente.' : 'Rol actualizado.'));
    } catch (error) {
      setMessageSuccess(false);
      setMessage(error.message);
    } finally { setBusy(false); }
  };

  const revokeInvitation = async invitation => {
    if (!window.confirm(`¿Cancelar la invitación de ${invitation.email}?`)) return;
    setBusy(true);
    try {
      await request(`/invitations/${encodeURIComponent(invitation._id)}`, { method: 'DELETE' });
      await refresh();
      setMessage('Invitación cancelada correctamente.');
      setMessageSuccess(true);
    } catch (error) { setMessage(error.message); setMessageSuccess(false); }
    finally { setBusy(false); }
  };

  const selectExisting = user => {
    // Roles retirados se muestran en el historial, pero nunca se precargan
    // como nuevas asignaciones. No se modifica la cuenta hasta Guardar rol.
    const currentAssignable = [...new Set([user.role, ...(user.roles || [])])]
      .filter(role => role !== 'admin_notaria' && Object.prototype.hasOwnProperty.call(labels, role));
    const next = {
      email: user.email, role: team ? 'abogado_notaria' : (currentAssignable[0] || ''),
      notarioId: user.notarioId || '',
      additionalRoles: team ? [] : currentAssignable.slice(1),
    };
    markRoleSaved(next);
    setForm(next);
  };

  return (
    <section className="wrm-card" aria-label={team ? 'Equipo de la notaría' : 'Usuarios y roles'}>
      {!embedded && <header className="wrm-header">
        <span className="wrm-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 3 4 6v6c0 5 8 9 8 9s8-4 8-9V6l-8-3Z"/><path d="m8.5 12 2.5 2.5 4.5-5"/></svg></span>
        <div><span className="wrm-eyebrow">Gestión de accesos</span><h3>{team ? 'Equipo de la notaría' : 'Usuarios y roles'}</h3><p>{team ? 'Invita o registra exclusivamente abogados de tu notaría.' : 'Asigna a cada persona el acceso que necesita para trabajar.'}</p></div>
        <span className="wrm-count">{data.users.length} usuarios</span>
      </header>}
      <form onSubmit={save} className="wrm-form">
        <label className="wrm-field wrm-email">Correo electrónico
          <input aria-label="Correo del usuario" type="email" required placeholder="nombre@empresa.com"
            value={form.email} onChange={e => { setForm({ ...form, email: e.target.value }); setMessage(''); }}
            autoComplete="off" />
        </label>
        <div className="wrm-field"><label htmlFor={roleInputId}>Rol asignado</label>
          <Select inputId={roleInputId} aria-label="Rol asignado" className="wrm-role-select"
            classNamePrefix="wrm-role-select" isMulti required isDisabled={busy}
            closeMenuOnSelect={false} hideSelectedOptions={false} options={roleOptions}
            value={selectedRoles.map(value => ({ value, label: labels[value] }))}
            onChange={options => {
              const values = (options || []).map(option => option.value);
              const primary = values.includes(form.role) ? form.role : (values[0] || '');
              setForm({ ...form, role: primary, additionalRoles: values.filter(value => value !== primary) });
            }}
            placeholder="Selecciona uno o varios roles" noOptionsMessage={() => 'No hay roles disponibles'} unstyled />
        </div>
        {!team && selectedRoles.some(role => ['notario', 'abogado_notaria'].includes(role)) &&
          <label className="wrm-field">Notaría
            <select aria-label="Notaría" required value={form.notarioId} onChange={e => setForm({ ...form, notarioId: e.target.value })}>
              <option value="">Seleccionar notaría</option>
              {data.notarios.map(n => <option key={n._id} value={n._id}>{n.nombre} · {n.numero}</option>)}
            </select>
          </label>}
        <button className="wrm-primary" disabled={busy || lookupStatus === 'idle' || lookupStatus === 'loading' || lookupStatus === 'error'}>
          {busy ? 'Procesando…' : lookupStatus === 'missing' ? 'Enviar invitación' : lookupStatus === 'loading' ? 'Comprobando…' : 'Guardar rol'}
          <span aria-hidden="true">→</span>
        </button>
        <p className="wrm-hint" role="status">
          {lookupStatus === 'missing' ? 'Este correo todavía no tiene cuenta. Se enviará una invitación para registrarse con los roles seleccionados.' :
            lookupStatus === 'existing' ? 'Cuenta encontrada: el rol se asignará directamente, sin aceptación.' :
            lookupStatus === 'loading' ? 'Comprobando si el correo ya está registrado…' :
            lookupStatus === 'error' ? 'No se pudo comprobar el correo. Inténtalo de nuevo.' :
            'Escribe un correo para comprobar si tiene una cuenta en Wisby.'}
          {!team && ' El notario titular debe estar vinculado al registro de Notarios.'}
        </p>
      </form>
      {message && <p className={`wrm-message ${messageSuccess ? 'is-success' : 'is-error'}`} role="status">{message}</p>}
      <div className="wrm-list-heading"><h4>{team ? 'Miembros del equipo' : 'Usuarios registrados'}</h4><span>{data.users.length} usuarios · Accesos y permisos</span></div>
      <div className="wrm-table-wrap">
        <table className="wrm-table"><thead><tr><th>Usuario</th><th>Rol</th><th><span className="wrm-action-heading">Administrar</span></th></tr></thead>
          <tbody>{data.users.map(user => <tr key={user._id}>
            <td><div className="wrm-user">
              <span className="wrm-avatar" aria-hidden="true">{(user.displayName || user.name || user.email || '?').charAt(0).toUpperCase()}</span>
              <div>{(user.displayName || user.name) && <strong>{user.displayName || [user.name, user.lastName].filter(Boolean).join(' ')}</strong>}<span>{user.email}</span></div>
            </div></td>
            <td><span className={`wrm-badge wrm-badge-${user.role}`}>{[...new Set([user.role, ...(user.roles || [])])].map(role => labels[role] || role).join(' · ')}</span></td>
            <td className="wrm-action-cell"><button type="button" className="wrm-secondary" aria-label={`Editar rol de ${user.email}`} onClick={() => guardAdminNavigation(() => selectExisting(user))}>Editar rol</button></td>
          </tr>)}</tbody>
        </table>
        {data.users.length === 0 && <div className="wrm-empty"><strong>No hay usuarios para mostrar</strong><p>Los usuarios disponibles aparecerán aquí para administrar sus roles.</p></div>}
      </div>
      <div className="wrm-list-heading"><h4>Invitaciones pendientes</h4><span>{invitations.length} por aceptar</span></div>
      {invitations.length > 0 ? <div className="wrm-table-wrap">
        <table className="wrm-table"><thead><tr><th>Correo</th><th>Roles invitados</th><th>Vence</th><th>Administrar</th></tr></thead>
          <tbody>{invitations.map(invitation => <tr key={invitation._id}>
            <td>{invitation.email}</td>
            <td>{[invitation.role, ...(invitation.additionalRoles || [])].map(role => labels[role] || role).join(' · ')}</td>
            <td>{new Date(invitation.expiresAt).toLocaleDateString('es-MX')}</td>
            <td className="wrm-action-cell"><button type="button" className="wrm-secondary" disabled={busy}
              onClick={() => revokeInvitation(invitation)}>Cancelar invitación</button></td>
          </tr>)}</tbody>
        </table>
      </div> : <div className="wrm-empty">No hay invitaciones pendientes.</div>}
      {!team && <AdminRolePreview users={data.users} />}
    </section>
  );
}
